import { r as createLogger } from "./logger-CdXeOlrX.js";
import "./archive-limits-CMo2zcn-.js";
import "./capped-zip-entries-CIyZmVN5.js";
import "./storage/zip-reader.js";
import { writeFileOrAbort } from "./storage/write-file-or-abort.js";
import "./storage/opfs-storage.js";
import "./storage/opfs-storage-backend.js";
import { n as keyForFileName, t as fileNameFor } from "./opfs-file-names-C9tOJg60.js";
import { downloadBlob } from "./storage/zip-export.js";
import "./storage/pack-files-as-zip.js";
import "./storage/zip-byte-source-reader.js";
import "./storage/zip-rebuild.js";
import "./storage/zip-coverage-embed.js";
import "./remote-range-byte-source-DW-eEL4J.js";
import "./storage/share-link.js";
import "./storage/open-remote-archive.js";
//#region ../src/storage/opfs-draft-store.ts
/**
* A flat, key-addressed file store in one OPFS directory.
*
* WHY THIS EXISTS SEPARATELY FROM `opfs-storage.ts`. That module is
* recording-session shaped - sessions, actions, frames, a metadata schema.
* What an authoring draft needs is a namespace and a handful of named
* files, appended one at a time, which is a different thing wearing the
* same API.
*
* WHY KEYS ARE ESCAPED RATHER THAN NESTED, and why the escaping is
* imported rather than written again: creating nested directories from a
* caller-supplied string is how a `..` segment becomes a traversal. That
* reasoning, and its inverse for listing, already exist for the OSM tile
* store; sharing them means one escaping contract rather than two that can
* drift (see `opfs-file-names.ts`).
*
* EVERY METHOD DEGRADES RATHER THAN THROWING. A draft is a safety net: a
* store that throws would turn "your work is also saved" into "your tap
* failed", which is worse than no net at all. `put` reports whether it
* persisted so a caller can say so once; nothing else reports anything.
*/
const log$1 = createLogger("OpfsDraftStore");
/** The subdirectory of the app's OPFS root that drafts live in. */
const DRAFT_STORE_DIR = "drafts";
/**
* Open (creating if needed) one namespace's directory under `root`.
*
* The namespace is escaped into a single directory name for the same
* reason keys are: it comes from a URL the creator pasted.
*
* Returns `undefined` when OPFS is unavailable or refuses - a browser
* without it, a quota wall, a context where storage is blocked. Callers
* treat that as "no persistence", never as an error.
*/
async function openDraftNamespace(root, namespace) {
	try {
		return createDraftFileStore(await (await root.getDirectoryHandle(DRAFT_STORE_DIR, { create: true })).getDirectoryHandle(encodeURIComponent(namespace), { create: true }));
	} catch (err) {
		log$1.warn("draft storage unavailable:", err);
		return;
	}
}
/**
* The store over an already-resolved directory. Exported so the tests can
* pass a fake handle and stay in node, as the OSM store's do.
*
* It took a `parent` and its own name until r669, so `clear` could remove
* the whole directory in one call. That is why it no longer does: removing
* the directory invalidates this handle, and every later write then failed
* silently. The parameters went with the capability rather than lingering
* as a signature nobody could safely use.
*/
function createDraftFileStore(directory) {
	return {
		async put(key, data) {
			try {
				await writeFileOrAbort(await directory.getFileHandle(fileNameFor(key), { create: true }), data);
				return true;
			} catch (err) {
				log$1.warn("draft write failed:", err);
				return false;
			}
		},
		async getText(key) {
			return (await readFile(directory, key))?.text();
		},
		async getBlob(key) {
			const file = await readFile(directory, key);
			return file === void 0 ? void 0 : file;
		},
		async keys() {
			const out = [];
			try {
				for await (const name of directory.keys()) {
					const key = keyForFileName(name);
					if (key !== void 0) out.push(key);
				}
			} catch (err) {
				log$1.warn("draft listing failed:", err);
			}
			return out;
		},
		async remove(key) {
			await directory.removeEntry(fileNameFor(key)).catch((err) => {
				if (err?.name !== "NotFoundError") log$1.warn("draft remove failed:", err);
			});
		},
		async clear(firstKey) {
			try {
				if (firstKey !== void 0) await directory.removeEntry(fileNameFor(firstKey)).catch(() => void 0);
				const names = [];
				for await (const name of directory.keys()) names.push(name);
				for (const name of names) await directory.removeEntry(name, { recursive: true });
			} catch (err) {
				log$1.warn("draft clear failed:", err);
			}
		}
	};
}
async function readFile(directory, key) {
	try {
		return await (await directory.getFileHandle(fileNameFor(key))).getFile();
	} catch {
		return;
	}
}
//#endregion
//#region ../src/storage/share-or-download.ts
/**
* `shareOrDownloadBlob` - hand a file to the device's share sheet when the
* browser can share FILES, and fall back to the save-or-download path
* otherwise.
*
* Why it lives here and not in an app: the recorder has done exactly this
* since 2026-02-06 (owner feedback), the Tour Viewer's finish step now
* needs it, and the behaviour carries a CONTRACT - what the user is told
* happened - which DEC-H3 says is unified rather than copied. Before this
* module there were two hand-written `<a download>` fallbacks in the
* repository and one hand-written share dance; the repo's duplicate guard
* is keyed on NAMES and could see none of it.
*
* The result is TWO independent questions, not one enum:
* - `route` - which mechanism ran. The caller's copy differs, because a
*   share creates a NEW file in the target app while a save replaces one.
* - `delivered` - whether anything left the page at all. `downloadBlob`
*   already answered this for the save picker and the Tour Viewer's finish
*   already branches on it, so folding it into a single enum would have
*   deleted a distinction the UI depends on.
*
* Two things `delivered` deliberately does NOT tell you, both properties of
* the Web Share API rather than of this module:
* - `delivered: false` on the share route means the file did not leave. It
*   does NOT mean the user cancelled: the Web Share algorithm rejects with
*   `AbortError` both for a cancelled sheet and for a failed share, and
*   they cannot be told apart. Copy must not accuse the user.
* - `delivered: true` on the share route means the data reached the app the
*   user picked, and nothing about what that app did with it. Copy must not
*   claim the file is in anyone's cloud folder.
*/
const log = createLogger("ShareOrDownload");
function navigatorCanShare() {
	if (typeof navigator === "undefined") return null;
	if (typeof navigator.canShare !== "function") return null;
	return (data) => navigator.canShare(data);
}
function navigatorShare() {
	if (typeof navigator === "undefined") return null;
	if (typeof navigator.share !== "function") return null;
	return (data) => navigator.share(data);
}
/**
* Is a share sheet the RIGHT hand-off here, not merely a possible one?
*
* This is the question every caller actually has, and it is not the same as
* `canShareFilesOfType`. Desktop Chrome (Windows, macOS) and macOS Safari
* implement Web Share, files included - checked against MDN and caniuse
* rather than assumed, because a behaviour change rests on it; Firefox and
* Linux Chrome are the exceptions. So the capability alone says yes on a
* desktop - where
* the share sheet offers Mail and Nearby Share and NO "save to disk", and
* the file the user is about to be told to upload somewhere never lands on
* their disk at all. The save picker is the better hand-off there, and it
* is the one this product's own instructions assume.
*
* On a phone the reverse holds: a download drops the file in Downloads and
* leaves the user to find it in another app, which is the friction the
* share sheet removes.
*
* So the policy is the capability AND a coarse primary pointer. That is a
* heuristic - a touchscreen laptop answers yes - and it is the right kind
* of wrong: such a device still has a share sheet, and its user still gets
* a working hand-off. Getting it backwards on a mouse-driven desktop is
* the expensive direction, because that is where the file has to reach a
* folder.
*/
function prefersFileShare(fileType, deps = {}) {
	return canShareFilesOfType(fileType, deps) && hasCoarsePointer(deps);
}
function hasCoarsePointer(deps) {
	if (deps.coarsePointer !== void 0) return deps.coarsePointer();
	if (typeof matchMedia !== "function") return false;
	try {
		return matchMedia("(pointer: coarse)").matches;
	} catch {
		return false;
	}
}
/**
* Can this browser share a file of `fileType` at all? The raw CAPABILITY -
* most callers want `prefersFileShare` instead.
*
* For LABELLING a button before the file exists. "Share" on a desktop that
* cannot share files is a lie, and "Download" on a phone that can is a
* worse description of what the button does - so the label is decided from
* the capability, once, rather than guessed from the platform.
*
* `canShare` is evaluated against real `File` objects, and at wire time
* there is no file yet, so the probe uses a one-byte dummy with the right
* name and MIME type. It is never shared.
*
* Returns false wherever the API, `navigator` or `File` is missing - which
* covers node, and is why the seam layer can call it during boot.
*/
function canShareFilesOfType(fileType, deps = {}) {
	const canShare = deps.canShare ?? navigatorCanShare();
	if (canShare === null) return false;
	if (deps.share === void 0 && navigatorShare() === null) return false;
	if (typeof File === "undefined") return false;
	try {
		return canShare({ files: [new File([new Uint8Array(1)], `probe${fileType.extension}`, { type: fileType.mimeType })] });
	} catch (err) {
		log.warn(`canShare probe threw for ${fileType.mimeType}, treating as no share:`, err instanceof Error ? err.message : String(err));
		return false;
	}
}
/**
* `canShare` with a real file, answering FALSE rather than throwing.
*
* Guarded like the wire-time probe is, and for the same reason: a
* `canShare` that throws must mean "no share here", not "this module
* rejects". Without it the module's own contract - it never rejects for a
* share problem, only for a failing download - was false on one line
* (PR #439 review #2).
*/
function canShareFile(canShare, file, filename) {
	try {
		return canShare({ files: [file] });
	} catch (err) {
		log.warn(`canShare threw for ${filename}, falling back to download:`, err instanceof Error ? err.message : String(err));
		return false;
	}
}
/**
* The share attempt, split out so the public function stays one decision
* deep: a `ShareOrDownloadResult` when the share route SETTLED the
* question (handed over, or aborted), or `null` meaning "not shareable
* here, or the share failed in a way a download can still rescue".
*/
async function tryShare(blob, filename, fileType, deps) {
	const share = deps.share ?? navigatorShare();
	const canShare = deps.canShare ?? navigatorCanShare();
	if (share === null || canShare === null || typeof File === "undefined") return null;
	if (!hasCoarsePointer(deps)) return null;
	const file = new File([blob], filename, { type: fileType.mimeType });
	if (!canShareFile(canShare, file, filename)) return null;
	try {
		await share({ files: [file] });
		log.info(`Shared ${filename} via the Web Share API`);
		return {
			route: "share",
			delivered: true
		};
	} catch (err) {
		const error = err;
		if (error.name === "AbortError") {
			log.info(`Share of ${filename} ended without handing the file over`);
			return {
				route: "share",
				delivered: false
			};
		}
		log.warn(`Web Share failed for ${filename}, falling back to download:`, error.message);
		return null;
	}
}
/**
* Share `blob` as a file if the browser can, else save or download it.
*
* Never throws for a cancelled share or a dismissed picker - those are
* `delivered: false`. A share that fails for any other reason falls back to
* the download route rather than surfacing, because the file the user asked
* for is still deliverable that way; only a failing DOWNLOAD rejects, which
* is `downloadBlob`'s existing contract.
*/
async function shareOrDownloadBlob(blob, filename, fileType, deps = {}) {
	const shared = await tryShare(blob, filename, fileType, deps);
	if (shared !== null) return shared;
	return {
		route: "download",
		delivered: await (deps.download ?? downloadBlob)(blob, filename, fileType)
	};
}
//#endregion
export { createDraftFileStore as a, DRAFT_STORE_DIR as i, prefersFileShare as n, openDraftNamespace as o, shareOrDownloadBlob as r, canShareFilesOfType as t };
